import { useState, useCallback } from 'react';
import { useMutation } from 'react-query';
import { VideoAssetUsage } from '@goldcast/api/backend';
import { ClipDeletionError } from './ClipDeletionErrorDialog';
import { bulkDeleteContentClips } from '@/libs/clipContentUtil';
import useDialog from '@/components/organisms/useDialog';

const NAVIGATION_ROUTES = {
  EVENT: {
    Recording: '/recording-event',
    Conference: '/events',
    Webinar: '/webinar'
  },
  HUB: '/pages',
  STUDIO_EVENT_TEMPLATE: '/templates'
} as const;

export type VideoAssetUsageWithUrl = VideoAssetUsage & { url: string | null };

function isVideoAssetUsage(obj: unknown): obj is VideoAssetUsage {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as VideoAssetUsage).entity_id === 'string' &&
    typeof (obj as VideoAssetUsage).entity_type === 'string'
  );
}

function getUsageUrl(usage: VideoAssetUsage): string | null {
  const origin = window.location.origin;
  if (usage.entity_type === 'EVENT') {
    const subPath = NAVIGATION_ROUTES.EVENT[usage.entity_subtype as keyof typeof NAVIGATION_ROUTES.EVENT];
    return subPath ? `${origin}${subPath}/${usage.entity_id}` : null;
  }
  if (usage.entity_type === 'HUB') {
    return `${origin}${NAVIGATION_ROUTES.HUB}/${usage.entity_id}`;
  }
  if (usage.entity_type === 'STUDIO_EVENT_TEMPLATE') {
    return `${origin}${NAVIGATION_ROUTES.STUDIO_EVENT_TEMPLATE}/${usage.entity_id}`;
  }
  return null;
}

export default function useBulkDelete({
  selectedClipIds,
  exitMultiSelectMode,
  onDelete,
  setClipDeletionError
}: {
  selectedClipIds: Set<string>;
  exitMultiSelectMode: () => void;
  onDelete: (clipId: string) => void;
  setClipDeletionError: (error: ClipDeletionError) => void;
}) {
  const [bulkDeleteInUseUsages, setBulkDeleteInUseUsages] = useState<VideoAssetUsageWithUrl[]>([]);

  const {
    isOpen: isBulkConfirmDeleteDialogOpen,
    openDialog: openBulkConfirmDeleteDialog,
    closeDialog: closeBulkConfirmDeleteDialog
  } = useDialog();

  const {
    isOpen: isBulkDeleteUsageDialogOpen,
    openDialog: openBulkDeleteUsageDialog,
    closeDialog: closeBulkDeleteUsageDialog
  } = useDialog();

  const { mutate: bulkDeleteClips, isLoading: isBulkDeleting } = useMutation({
    mutationFn: (clipIds: string[]) => bulkDeleteContentClips(clipIds),
    onSuccess: ({ deleted, failed }) => {
      deleted.forEach(clipId => onDelete(clipId));
      if (failed.length > 0) {
        const inUseFailed = failed.filter(f => f.reason === 'CLIP_IN_USE');
        const otherFailed = failed.filter(f => f.reason !== 'CLIP_IN_USE');

        if (inUseFailed.length > 0) {
          const combinedUsages = inUseFailed
            .flatMap(f => (f.usages ?? []).filter(isVideoAssetUsage))
            .map(u => ({ ...u, url: getUsageUrl(u) }));
          setBulkDeleteInUseUsages(combinedUsages);
          openBulkDeleteUsageDialog();
        }

        if (otherFailed.length > 0) {
          setClipDeletionError({
            clipId: 'bulk',
            message: `${otherFailed.length} clip(s) could not be deleted. Please try again.`
          });
        }
      }
      exitMultiSelectMode();
      closeBulkConfirmDeleteDialog();
    },
    onError: () => {
      setClipDeletionError({
        clipId: 'bulk',
        message: 'Failed to delete clips. Please try again.'
      });
      closeBulkConfirmDeleteDialog();
    }
  });

  const onBulkDeleteClips = useCallback(() => {
    bulkDeleteClips(Array.from(selectedClipIds));
  }, [selectedClipIds, bulkDeleteClips]);

  return {
    isBulkDeleting,
    isBulkConfirmDeleteDialogOpen,
    openBulkConfirmDeleteDialog,
    closeBulkConfirmDeleteDialog,
    isBulkDeleteUsageDialogOpen,
    closeBulkDeleteUsageDialog,
    bulkDeleteInUseUsages,
    onBulkDeleteClips
  };
}
